import { ActivityViewModel } from '../ViewModels';
import { ActivityHeader } from '../activity-config';
import { useActivityEnricher } from './useActivityEnricher';
import {
    ActionStatusHelper,
    DomainTableConfig,
    useAppEnum,
    ActionResponse,
    useLayoutLifeCycle,
} from '@/Shared';
import { DOMAIN_CONFIGS, DomainHookProps } from '@/Shared/Config/domain-config';
import { useActivity } from '@/OneLiners';
import { ActivityModel } from '../Models';


/**
 * useActivityTableConfig hook
 *
 * Domain-specific layout composition hook for Activity entities.
 * Acts as the composition layer between:
 * - Activity domain data access,
 * - enrichment logic and derived field resolution,
 * - permission state aggregation,
 * - and the generic layout lifecycle infrastructure.
 *
 * Responsibilities:
 * - Combine domain and enrichment readiness states into a single contract.
 * - Provide dynamic header configuration for the Activity table.
 * - Define strict Model → ViewModel transformation.
 * - Delegate lifecycle orchestration to useLayoutLifeCycle.
 *
 * This hook is declarative by design.
 * It specifies domain dependencies and transformations,
 * while lifecycle mechanics remain centralized in infrastructure.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 *
 * @see DomainHookProps
 * @see DomainTableConfig<ActivityViewModel>
 *
 * @param props Domain-level configuration and permissions
 * @returns DomainTableConfig<ActivityViewModel>
 */
export function useActivityTableConfig(props: DomainHookProps): DomainTableConfig<ActivityViewModel> {

    const { permissionHook, accessRight } = props;
    const { domainType } = DOMAIN_CONFIGS.Activity;

    /**
    * Activity domain entry point.
    */
    const activityHook = useActivity();

    /**
     * Activity-related enums exposed to the UI layer.
     */
    const { ActivityEnums } = useAppEnum();

    /**
     * Activity enrichment layer.
     *
     * Responsible for resolving user relationships
     * and computing derived Activity fields.
     */
    const {
        userMap,
        getStrictEnrichedActivity,
        enricherResponse,
    } = useActivityEnricher();

    /**
     * Combined readiness response.
     *
     * The layout lifecycle will only initialize once
     * this contract reaches a succeeded state.
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            activityHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status,
            //accessRight.status
        ),
        error: activityHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
     * Delegation to generic layout lifecycle.
     */
    const lifeCycleHook = useLayoutLifeCycle({
        response,
        domainType,
        props,
        entityHook: activityHook,

        /**
         * Header configuration for Property table.
         */
        headers: ActivityHeader.build(
            userMap,
            ActivityEnums
        ),

        /**
         * Strict Model → ViewModel transformation.
         */
        toViewModelFn: (
            activity: ActivityModel, isDisplaying: boolean) =>
            new ActivityViewModel({
                activity: getStrictEnrichedActivity(activity),
                viewEnums: ActivityEnums,
                accessRight,
                isDisplaying
            })
    });

    return lifeCycleHook;
}